import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {Map as MapIcon} from '@mui/icons-material';
import {Alert, Box, Button, FormControl, InputLabel, MenuItem, Select, Slider, Stack, Typography} from '@mui/material';
import {gql} from '@apollo/client';
import {useApolloClient} from '@apollo/client/react';
import {CircleMarker, Polyline, useMap} from 'react-leaflet';
import {LatLngExpression} from 'leaflet';
import L from 'leaflet';
import dayjs from 'dayjs';

import ActionMenuItem, {ActionMenuItemProps} from './ActionMenuItem';
import {useRecordContext} from 'components/record/useRecordContext';
import CustomDialog from 'components/forms/CustomDialog';
import {DialogContextProvider} from 'components/dialog/DialogContext';
import MapContainer from 'components/common/MapContainer';

const GPS_HISTORY_BATCH_LIMIT = 5000;
const GPS_HISTORY_MAX_BATCHES = 200;

const DEVICE_GPS_HISTORY = gql`
  query DeviceGpsHistory($deviceName: String!, $from: Date!, $to: Date!, $limit: Int, $cursor: String) {
    deviceGpsHistory(deviceName: $deviceName, from: $from, to: $to, limit: $limit, cursor: $cursor) {
      points {
        time
        lat
        lng
        alt
      }
      hasMore
      nextCursor
    }
  }
`;

type GpsPoint = {lat: number; lng: number; time: number; alt?: number};

const DURATION_OPTIONS = [
  {label: 'Last 24 hours', ms: 24 * 60 * 60 * 1000},
  {label: 'Last 7 days', ms: 7 * 24 * 60 * 60 * 1000},
  {label: 'Last 14 days', ms: 14 * 24 * 60 * 60 * 1000}
] as const;

function FitTrackBounds({positions}: {positions: LatLngExpression[]}) {
  const map = useMap();
  useEffect(() => {
    if (positions.length === 0) {
      return;
    }
    if (positions.length === 1) {
      const p = positions[0] as [number, number];
      map.setView(p, 14);
      return;
    }
    const bounds = L.latLngBounds(positions as [number, number][]);
    map.fitBounds(bounds, {padding: [28, 28], maxZoom: 16});
  }, [map, positions]);
  return null;
}

const ActionMenuItemPreviewDevice = (props: ActionMenuItemPreviewDeviceProps): JSX.Element | null => {
  const {label = 'Preview GPS', sx, icon = <MapIcon />, onClose, ...rest} = props;
  const {record: device} = useRecordContext();
  const client = useApolloClient();

  const [open, setOpen] = useState(false);
  const [durationMs, setDurationMs] = useState<number>(DURATION_OPTIONS[2].ms);
  const [points, setPoints] = useState<GpsPoint[]>([]);
  const [sliderIndex, setSliderIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleDialogOpen: React.MouseEventHandler = (e) => {
    setOpen(true);
    e.stopPropagation();
  };

  const handleClose = () => {
    if (open) {
      setOpen(false);
      onClose?.();
    }
  };

  const mapCenter = useMemo((): LatLngExpression => {
    if (points.length > 0) {
      return [points[0].lat, points[0].lng];
    }
    return [50.08, 14.42];
  }, [points]);

  const mapZoom = points.length > 0 ? 13 : 8;

  const polylinePositions = useMemo(() => points.map((p) => [p.lat, p.lng] as LatLngExpression), [points]);

  const currentPoint = points[sliderIndex];

  const loadTrack = useCallback(async () => {
    const deviceName = device?.name;
    if (!deviceName) {
      setError('Missing device name.');
      return;
    }
    setLoading(true);
    setError(null);

    const now = Date.now();
    const from = now - durationMs;

    try {
      type HistoryPage = {
        points: Array<{time: string | number; lat: number; lng: number; alt: number | null}>;
        hasMore: boolean;
        nextCursor: string | null;
      };

      const rows: HistoryPage['points'] = [];
      let cursor: string | undefined;

      for (let batch = 0; batch < GPS_HISTORY_MAX_BATCHES; batch += 1) {
        const res = await client.query<{
          deviceGpsHistory: HistoryPage;
        }>({
          query: DEVICE_GPS_HISTORY,
          variables: {
            deviceName,
            from,
            to: now,
            limit: GPS_HISTORY_BATCH_LIMIT,
            cursor: cursor ?? null
          },
          fetchPolicy: 'network-only',
          errorPolicy: 'all'
        });

        if (res.error) {
          setPoints([]);
          setError(res.error.message);
          return;
        }

        const page = res.data?.deviceGpsHistory;
        if (!page?.points?.length) {
          break;
        }

        rows.push(...page.points);

        if (!page.hasMore || !page.nextCursor) {
          break;
        }

        cursor = page.nextCursor;
      }

      if (!rows.length) {
        setPoints([]);
        setError('No GPS points found for this device in the selected time range.');
        return;
      }

      const parsed: GpsPoint[] = rows.map((p) => ({
        lat: p.lat,
        lng: p.lng,
        time: new Date(p.time).getTime(),
        alt: p.alt != null ? p.alt : undefined
      }));
      setPoints(parsed);
      setSliderIndex(Math.max(0, parsed.length - 1));
    } catch (e) {
      setPoints([]);
      setError(e instanceof Error ? e.message : 'Failed to load GPS data.');
    } finally {
      setLoading(false);
    }
  }, [client, durationMs, device?.name]);

  useEffect(() => {
    if (!open) {
      return;
    }
    setError(null);
  }, [open]);

  return (
    <React.Fragment>
      <ActionMenuItem onClick={handleDialogOpen} label={label} sx={sx} icon={icon} {...rest} />
      <DialogContextProvider>
        <CustomDialog
          dialogTitle={`GPS preview — ${device?.name ?? 'device'}`}
          maxWidth="lg"
          primaryButton={
            <Button variant="contained" onClick={handleClose}>
              Close
            </Button>
          }
          open={open}
          resetDialog={handleClose}
        >
          <Stack spacing={2} sx={{pt: 1}}>
            <Stack direction={{xs: 'column', sm: 'row'}} spacing={2} alignItems={{sm: 'flex-end'}}>
              <FormControl size="small" sx={{minWidth: 200}}>
                <InputLabel id="device-preview-duration">Time range</InputLabel>
                <Select
                  labelId="device-preview-duration"
                  label="Time range"
                  value={durationMs}
                  onChange={(e) => setDurationMs(Number(e.target.value))}
                >
                  {DURATION_OPTIONS.map((opt) => (
                    <MenuItem key={opt.ms} value={opt.ms}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button variant="contained" onClick={() => void loadTrack()} disabled={loading}>
                {loading ? 'Loading…' : 'Load track'}
              </Button>
            </Stack>

            {error && <Alert severity="error">{error}</Alert>}

            <Box sx={{height: 420, width: '100%', position: 'relative', borderRadius: 1, overflow: 'hidden'}}>
              <MapContainer center={mapCenter} zoom={mapZoom} style={{width: '100%', height: '100%', position: 'relative'}}>
                <FitTrackBounds positions={polylinePositions} />
                {polylinePositions.length > 1 && <Polyline pathOptions={{color: '#1976d2', weight: 4}} positions={polylinePositions} />}
                {currentPoint && (
                  <CircleMarker
                    center={[currentPoint.lat, currentPoint.lng]}
                    radius={10}
                    pathOptions={{color: '#d32f2f', fillColor: '#ff5252', fillOpacity: 0.9, weight: 2}}
                  />
                )}
              </MapContainer>
            </Box>

            {points.length > 0 && (
              <Stack spacing={1}>
                <Typography variant="body2" color="text.secondary">
                  Full track is shown in blue; the red dot is the position at the selected time (
                  {dayjs(currentPoint?.time || 0).format('YYYY-MM-DD HH:mm:ss')})
                </Typography>
                <Slider
                  size="small"
                  min={0}
                  max={points.length - 1}
                  value={sliderIndex}
                  onChange={(_, v) => setSliderIndex(v as number)}
                  valueLabelDisplay="auto"
                  valueLabelFormat={(i) => dayjs(points[i]?.time || 0).format('HH:mm:ss')}
                />
              </Stack>
            )}
          </Stack>
        </CustomDialog>
      </DialogContextProvider>
    </React.Fragment>
  );
};

export default ActionMenuItemPreviewDevice;

type ActionMenuItemPreviewDeviceProps = Omit<ActionMenuItemProps, 'onClick'> & {
  onClose?: () => void;
};
